import React, { createContext, useContext, useState, useEffect, useMemo, ReactNode } from 'react';
import { useDemoMode } from './DemoModeContext';

export type DemoPath = 'surgeon' | 'admin' | 'compliance';

interface DemoPathContextType {
  demoPath: DemoPath;
  setDemoPath: (path: DemoPath) => void;
  isDemoMode: boolean;
  
  // Convenience helpers for visibility
  isSurgeonPath: boolean;
  isAdminPath: boolean;
  isCompliancePath: boolean;
  
  // Helper text for current path
  helperText: string;
}

const DEMO_PATH_STORAGE_KEY = 'link_demo_path';

const HELPER_TEXTS: Record<DemoPath, string> = {
  surgeon: "Approve care. LINK handles payer execution. Post-op, LINK confirms documentation supports what was authorized.",
  admin: "Reduce cancellations, protect revenue, standardize execution, create audit-ready authorization records.",
  compliance: "Surgeon-in-the-loop confirmations, immutable audit trail, role-based access, no clinical content generation.",
};

const DemoPathContext = createContext<DemoPathContextType | undefined>(undefined);

export function DemoPathProvider({ children }: { children: ReactNode }) {
  const { isDemoMode } = useDemoMode();
  
  const [demoPath, setDemoPathState] = useState<DemoPath>(() => {
    const stored = localStorage.getItem(DEMO_PATH_STORAGE_KEY);
    if (stored && ['surgeon', 'admin', 'compliance'].includes(stored)) {
      return stored as DemoPath;
    }
    return 'surgeon';
  });

  // Persist to localStorage
  const setDemoPath = (path: DemoPath) => {
    setDemoPathState(path);
    localStorage.setItem(DEMO_PATH_STORAGE_KEY, path);
  };

  const value = useMemo(() => ({
    demoPath,
    setDemoPath,
    isDemoMode,
    isSurgeonPath: demoPath === 'surgeon',
    isAdminPath: demoPath === 'admin',
    isCompliancePath: demoPath === 'compliance',
    helperText: HELPER_TEXTS[demoPath],
  }), [demoPath, isDemoMode]);

  return (
    <DemoPathContext.Provider value={value}>
      {children}
    </DemoPathContext.Provider>
  );
}

export function useDemoPath() {
  const context = useContext(DemoPathContext);
  if (!context) {
    throw new Error('useDemoPath must be used within a DemoPathProvider');
  }
  return context;
}

// Hook for conditional rendering based on demo path
export function useDemoPathVisibility() {
  const { isDemoMode, demoPath, isSurgeonPath, isAdminPath, isCompliancePath } = useDemoPath();
  
  return {
    isDemoMode,
    demoPath,
    
    // Panel visibility helpers
    shouldShowSurgeonPanels: !isDemoMode || isSurgeonPath,
    shouldShowAdminPanels: !isDemoMode || isAdminPath,
    shouldShowCompliancePanels: !isDemoMode || isCompliancePath,
    
    // Expansion state helpers (for demo mode)
    shouldExpandPhase2: isDemoMode && (isSurgeonPath || isCompliancePath),
    shouldExpandPhase3: isDemoMode && (isAdminPath || isCompliancePath),
    shouldExpandInsights: isDemoMode && !isSurgeonPath,
    
    // Emphasis helpers
    emphasizeAttestation: isDemoMode && isCompliancePath,
    emphasizeRevenueInsights: isDemoMode && isAdminPath,
    emphasizeAuditTrail: isDemoMode && isCompliancePath,
  };
}
